/**
 * PlayerCard — one of two cards in the left rail showing a player's name,
 * avatar, captured-rings count + 3-slot ring score, and a "Your turn" badge
 * when it's their turn.
 *
 * Mirrors the playground's PlayerCard but accepts an `isYou` flag so the
 * multiplayer client can subtly mark "you" vs "opponent".
 */

import { Badge } from "../../../ui";

interface PlayerCardProps {
  readonly name: string;
  readonly color: "WHITE" | "BLACK";
  readonly ringsScored: number;
  readonly isActive: boolean;
  readonly isYou: boolean;
}

function RingScoreSlots({
  scored,
  color,
}: {
  scored: number;
  color: "WHITE" | "BLACK";
}) {
  const fillStroke =
    color === "WHITE"
      ? "var(--cn-yinsh-piece-white)"
      : "var(--cn-yinsh-piece-black)";
  return (
    <span
      data-testid={`ring-score-${color.toLowerCase()}`}
      style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
    >
      {Array.from({ length: 3 }, (_, i) => {
        const filled = i < scored;
        return (
          <svg
            key={i}
            width={14}
            height={14}
            viewBox="0 0 14 14"
            aria-hidden="true"
          >
            <circle
              cx={7}
              cy={7}
              r={5}
              fill="none"
              stroke={filled ? fillStroke : "var(--cn-yinsh-tray-empty-slot)"}
              strokeWidth={filled ? 3 : 1.5}
              strokeDasharray={filled ? undefined : "2 2"}
            />
          </svg>
        );
      })}
    </span>
  );
}

export function PlayerCard({
  name,
  color,
  ringsScored,
  isActive,
  isYou,
}: PlayerCardProps) {
  const avatarBg =
    color === "WHITE"
      ? "var(--cn-yinsh-piece-white)"
      : "var(--cn-yinsh-piece-black)";

  return (
    <div
      data-testid={`player-card-${color.toLowerCase()}`}
      data-active={isActive ? "true" : "false"}
      style={{
        background: "var(--cn-surface-primary)",
        border: "1px solid var(--cn-border-subtle)",
        borderRadius: "var(--cn-radius-lg)",
        padding: 16,
        boxShadow: isActive
          ? "0 0 0 2px color-mix(in oklab, var(--cn-yinsh-selection) 65%, transparent), 0 0 18px color-mix(in oklab, var(--cn-yinsh-selection) 30%, transparent)"
          : "var(--cn-shadow-sm)",
        transition: "box-shadow 160ms ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 28,
              height: 28,
              borderRadius: 999,
              background: avatarBg,
              border: "2px solid var(--cn-yinsh-ring-border)",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontWeight: 600,
              color: "var(--cn-text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
            {isYou ? (
              <span
                style={{
                  marginLeft: 6,
                  color: "var(--cn-text-muted)",
                  fontWeight: 400,
                  fontSize: "0.8em",
                }}
              >
                (나)
              </span>
            ) : null}
          </span>
        </div>
        {isActive ? <Badge color="blue">내 차례</Badge> : null}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          fontSize: 13,
          color: "var(--cn-text-secondary)",
        }}
      >
        <span>
          링 <strong>{ringsScored}</strong> / 3
        </span>
        <RingScoreSlots scored={ringsScored} color={color} />
      </div>
    </div>
  );
}
